import React, { useEffect, useState } from 'react'
import { PageHeader } from '../../../shared/components'
import { faExclamationCircle, faEdit, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { LocationDetailDto } from 'appDtos';
import { Container, Card, Row, Col, Button } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { AdministrationPaths } from '../../../shared/api/constants';
import { AppPaths } from '../../../shared/appConstants';
import { AlertComponent } from '../../../shared/components';
import { BackButton } from '../../../shared/components';
import { ConfirmationModal } from '../../../shared/components';
import { Icon } from '../../../shared/components';
import { Loading } from '../../../shared/components';
import { useApi } from '../../../shared/hooks/useApi';
import { useApiHandleError } from '../../../shared/hooks/useApiHandleError';
import { formatDate } from '../../../shared/utils/utils';

export function LocationDetail() {
  const { id } = useParams();
  const [locationDetail, setLocationDetail] = useState<LocationDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const API = useApi();
  const { setError, errorMessage } = useApiHandleError();
  const goTo = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const response = await API.get(AdministrationPaths.GetLocationDetail(Number(id)));
        if (response.data) {
          setLocationDetail(response.data);
        }
      } catch (error) {
        setError(error)
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = () => {
    setLoading(true);
    setShowDeleteConfirmation(false);

    (async () => {
      try {
        await API.delete(AdministrationPaths.DeleteLocation(Number(id)));
        goTo(AppPaths.getLocations())
      } catch (error) {
        setError(error);
        setLoading(false);
      }
    })();
  };

  const deleteModalContent = (
    <div>
      <p>Are you sure you want to delete the <strong>{`${locationDetail?.name}`}</strong> location?</p>
      <p className="text-danger"> <Icon icon={faExclamationCircle} /> <strong>Important: </strong> This action cannot be undone.</p>
      <p>This action will permanently remove the selected item from the system. Any associated data or records may also be deleted.</p>
      <p>Please confirm your choice below:</p>
    </div>
  )

  return (
    <Container>
      <PageHeader pageTitle='Location Detail' />
      <AlertComponent message={errorMessage} />
      {loading ? (
        <Loading />
      ) : (
        locationDetail ? (
          <Card className="border border-1 border-success">
            <Card.Header>
              <Card.Title className='display-5 fw-bold'>{locationDetail.name}</Card.Title>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md="3">
                  <Card.Subtitle className="mt-3">Address</Card.Subtitle>
                  <Card.Title className="h1 fw-bold">{locationDetail.address}</Card.Title>
                </Col>
                <Col md="3">
                  <Card.Subtitle className="mt-3">City/Town</Card.Subtitle>
                  <Card.Title className="h1 fw-bold">{locationDetail.cityOrTown}</Card.Title>
                </Col>
                <Col md="3">
                  <Card.Subtitle className="mt-3">County</Card.Subtitle>
                  <Card.Title className="h1 fw-bold">{locationDetail.county}</Card.Title>
                </Col>
                <Col md="3">
                  <Card.Subtitle className="mt-3">Created Date</Card.Subtitle>
                  <Card.Title className="h1 fw-bold">{formatDate(locationDetail.createdDate)}</Card.Title>
                </Col>
                <Col md="3">
                  <Card.Subtitle className="mt-3">Created By</Card.Subtitle>
                  <Card.Title className="h1 fw-bold">{locationDetail.createdBy ?? 'System'}</Card.Title>
                </Col>
                {Boolean(locationDetail.lastUpdatedDate) && <Col md="3">
                  <Card.Subtitle className="mt-3">Last Updated</Card.Subtitle>
                  <Card.Title className="h1 fw-bold">{formatDate(locationDetail.lastUpdatedDate)}</Card.Title>
                </Col>}
                {Boolean(locationDetail.lastUpdatedBy) && <Col md="3">
                  <Card.Subtitle className="mt-3">Last Updated By</Card.Subtitle>
                  <Card.Title className="h1 fw-bold">{locationDetail.lastUpdatedBy}</Card.Title>
                </Col>}
              </Row>
            </Card.Body>
          </Card>
        ) : null
      )}
      {!loading && <>
        <BackButton />
        <Button onClick={() => goTo(AppPaths.getEditLocation(Number(locationDetail?.locationId)))} variant="success" className="mx-2 mt-5 fw-bold">
          {<Icon icon={faEdit} />} Edit
        </Button>
        <Button onClick={() => setShowDeleteConfirmation(true)} variant="danger" className="mx-2 mt-5 fw-bold">
          {<Icon icon={faTrashCan} />} Delete
        </Button>
      </>}
      <ConfirmationModal
        message={deleteModalContent}
        okHandler={handleDelete}
        cancelHandler={() => setShowDeleteConfirmation(false)}
        show={showDeleteConfirmation}
      />
    </Container>
  )
}
